import { useFormStore } from "../store/formStore";
import { useLockStore } from "../store/lockStore";
import { lockApi } from "../api/lockApi";

/**
 * Release the form lock, then run `next` (sign-out redirect, group switch, …).
 *
 * The release happens BEFORE the navigation and is awaited. Without this we
 * rely on the beforeunload sendBeacon, which is unreliable for cross-origin
 * targets (Safari ITP, and the JSON content-type forces a CORS preflight that
 * browsers routinely drop during page unload) — leaving a pending requester
 * stuck waiting for a transfer push that never fires, until the 30s auto-grant
 * or 90s TTL. The beacon stays in place as a fallback for crashes / tab close.
 *
 * `next` always runs — a release failure must not strand the user on the form.
 * The server treats a session mismatch as a no-op, so a stale release can't
 * break another session's lock.
 */
export async function releaseLockThen(next: () => void | Promise<void>): Promise<void> {
  await releaseLock();
  await next();
}

/**
 * Releases the form lock, when a form is loaded. Never throws: a failed release must not stop what comes next (the 90s
 * TTL is the safety net). Sign out hands this to `signOut` so it runs once the tab is marked as leaving.
 */
export async function releaseLock(): Promise<void> {
  const formId = useFormStore.getState().formId;
  const sessionId = useLockStore.getState().sessionId;
  if (formId) {
    try {
      await lockApi.releaseLock(formId, sessionId);
    } catch {
      // Don't block the navigation on release failure — the 90s TTL is the safety net.
    }
  }
}
